import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FallbackError } from '@bloomays-lib/ui.shared';
import { TitlePart } from '@bloomays-lib/ui.shared';
import { styled } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import TableLegalContracts, { type SigningStateQuickFilter } from '../organisms/TableLegalContracts';
import { useQuery } from '@apollo/client';
import { ContractsByMissionGQL, GetLegalContractsByMissionGQLResult } from '@bloomays-lib/types.shared';
import { GET_LEGAL_CONTRACTS_BY_MISSION } from '@bloomays-lib/adapter.api-bloomer';
import { LoaderPending } from '@bloomays-lib/ui.shared';
import {
  computeGlobalContractSummary,
  computeMissionContractCounts,
  getPrivateAdminDashboardMissionPath,
} from '@bloomays-lib/utils.shared';
import { useBreadcrumbStore } from '../../stores';
import { buildMissionBreadcrumbLabel } from '../../helpers/breadcrumb';
import ContractsSummaryBanner from '../molecules/ContractsSummaryBanner';

const MissionContracts = ({ play = true }: { play?: boolean }) => {
  const { t } = useTranslation(['legalContracts']);
  const { id } = useParams<{ id: string }>();
  const missionId = id as string;
  const [signingStateFilter, setSigningStateFilter] = useState<SigningStateQuickFilter>('');

  const {
    data: fetchContractsResult,
    loading: fetchingContracts,
    error: errorFetchingContracts,
  } = useQuery<GetLegalContractsByMissionGQLResult>(GET_LEGAL_CONTRACTS_BY_MISSION, {
    variables: { missionId },
  });

  const mission = fetchContractsResult?.getLegalContractsByMission?.mission;
  const setBreadcrumbLabel = useBreadcrumbStore((state) => state.setLabel);
  const clearBreadcrumbLabel = useBreadcrumbStore((state) => state.clearLabel);

  // Le retour à la mission se fait via le fil d'Ariane : on y affiche le nom de la mission.
  useEffect(() => {
    const crumb = buildMissionBreadcrumbLabel({
      label: mission?.label,
      bloomerFullName: mission?.bloomerFullName,
      clientName: mission?.clientSocietyName,
    });
    if (!missionId || !crumb) return;
    const path = getPrivateAdminDashboardMissionPath(missionId);
    setBreadcrumbLabel(path, crumb);
    return () => clearBreadcrumbLabel(path);
  }, [
    missionId,
    mission?.label,
    mission?.bloomerFullName,
    mission?.clientSocietyName,
    setBreadcrumbLabel,
    clearBreadcrumbLabel,
  ]);

  if (fetchingContracts) return <LoaderPending text={t('fetchingMissionContracts')} rotate={play} />;
  if (errorFetchingContracts) return <FallbackError errorText={t('errorFetchingContracts')}></FallbackError>;
  if (!fetchContractsResult) return <>{t('missionNotFound')}</>;

  const contractsByMission: ContractsByMissionGQL = fetchContractsResult.getLegalContractsByMission;

  const summary = computeGlobalContractSummary([
    computeMissionContractCounts(contractsByMission.contracts ?? [], contractsByMission.mission.beginMission),
  ]);

  return (
    <Container>
      <TitlePart id="titleAdminDashboard" span={t('pageTitle')} />
      <BannerSlot>
        <ContractsSummaryBanner summary={summary} onSeeAll={() => setSigningStateFilter('toSign')} />
      </BannerSlot>
      <TableContainer>
        <TableLegalContracts
          missionsContracts={[contractsByMission]}
          signingStateFilter={signingStateFilter}
          onSigningStateFilterChange={setSigningStateFilter}
          noRowsLabel={t('noContractFounds')}
        />
      </TableContainer>
    </Container>
  );
};

export default MissionContracts;

const Container = styled('div')(() => ({
  margin: '0 80px',
}));

const BannerSlot = styled('div')(() => ({
  marginTop: 24,
}));

const TableContainer = styled('div')(() => ({
  marginTop: '24px',
}));
